import { apiClient } from "./api";
import { queryOptions } from "@tanstack/react-query";

export interface AgentBalance {
  agent_id: string;
  available_credits: number;
  lifetime_granted_credits: number;
  lifetime_consumed_credits: number;
  lifetime_purchased_credits: number;
  last_updated: string;
  next_expiration_date?: string;
  expiring_credits: number;
}

export interface PromoCredit {
  promo_id: string;
  amount: number;
  expiration_date?: string;
  created_at: string;
  created_by_user?: string;
  redeemed_by_agent_name?: string;
  redeemed_by_agent_id?: string;
  redeemed_at?: string;
  status: "available" | "redeemed" | "expired";
  metadata?: {
    created_by_email?: string;
  };
}

export interface PromoVoucherCreateRequest {
  promo_id: string;
  amount: number;
  expiration_days?: number;
  metadata?: Record<string, any>;
}

export interface CreditGrantRequest {
  agent_id: string;
  amount: number;
  reason?: string;
}

export interface RedeemPromoRequest {
  promo_id: string;
  agent_id: string;
}

// API Functions

// Redeem promo code
export const redeemPromoCode = async (
  data: RedeemPromoRequest,
): Promise<{ message: string }> => {
  try {
    const response = await apiClient.post("/api/credits/redeem-promo", data);
    return response.data;
  } catch (error) {
    console.error("Error redeeming promo code:", error);
    throw error;
  }
};

// Admin: Create promo code
export const createPromoCode = async (
  data: PromoVoucherCreateRequest,
): Promise<PromoCredit> => {
  try {
    const response = await apiClient.post("/api/credits/create-promo", data);
    return response.data;
  } catch (error) {
    console.error("Error creating promo code:", error);
    throw error;
  }
};

// Admin: Grant credits
export const grantCredits = async (
  data: CreditGrantRequest,
): Promise<AgentBalance> => {
  try {
    const response = await apiClient.post("/api/credits/grant", data);
    return response.data;
  } catch (error) {
    console.error("Error granting credits:", error);
    throw error;
  }
};

// Get agent balance
export const getAgentBalance = async (
  agentId: string,
): Promise<AgentBalance> => {
  try {
    const response = await apiClient.get(`/api/credits/balance/${agentId}`);
    return response.data;
  } catch (error) {
    console.error("Error fetching agent balance:", error);
    throw error;
  }
};

// api/credits.ts
// api/credits.ts
export const getPromoCodes = async (): Promise<PromoCredit[]> => {
  try {
    const response = await apiClient.get("/api/credits/promo-codes");

    // The response is already the array, no need to access .promo_codes
    return response.data;
  } catch (error) {
    console.error("Error fetching promo codes:", error);
    throw error;
  }
};

// React Query Options
export const creditQueries = {
  agentBalance: (agentId: string) =>
    queryOptions({
      queryKey: ["agent-balance", agentId],
      queryFn: () => getAgentBalance(agentId),
    }),
  promoCodes: () => ({
    queryKey: ["promo-codes"],
    queryFn: () =>
      getPromoCodes().catch((error) =>
        console.error("Error fetching promo codes:", error),
      ),
  }),
};
